import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Agency/Clients/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import { useTableConfig } from 'modules/Agency/Clients/subcomponents/hooks/useTableConfig'
import ShowMoreButton from 'components/ShowMoreButton'
import { Button, Grid, Icon, List } from 'gu-ui-kit'
import useSwitchedUser from 'utils/hooks/useSwitchedUser'
import { Link } from 'react-router-dom'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import styles from 'modules/Agency/agency.module.scss'
import EmptyResults from 'components/EmptyResults'
import { useTranslation } from 'react-i18next'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useHistory } from 'react-router'
import GridSpinner from 'components/UI/GridSpinner'
import DropdownSortingMenu from 'components/DropdownSortingMenu'
import AGENCY_API_V1 from 'api/agency/v1'
import AccountRow from 'modules/Agency/Clients/subcomponents/AccountRow'
import PageHeader from 'components/PageHeader'

const Clients = () => {
  const { cancelableDispatch } = useCancelToken()
  const { switchUser } = useSwitchedUser()
  const dispatch = useDispatch()
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const history = useHistory()
  const { loading, data, filters, pagination } = useSelector((state) => state['agency/clients'])

  const [sorting, setSorting] = useState({
    prop: 'priority',
    direction: 'asc',
  })

  const handleChangePage = useCallback(() => {
    setPage(page + 1)
  }, [setPage, page])

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    setAction: 'AGENCY/CLIENTS_SET_FILTER',
    localStorageKey: 'agency/clients',
  })

  useEffect(() => {
    dispatch({ type: 'AGENCY/CLIENTS_DROP_DATA' })
    setPage(1)
  }, [filterManager.params, sorting])

  useEffect(() => {
    filterManager.params &&
      cancelableDispatch({
        type: 'AGENCY/CLIENTS_FETCH',
        payload: {
          params: {
            ...filterManager.params,
            offset: pagination.limit * (page - 1),
            limit: pagination.limit,
            sort: sorting.prop,
            direction: sorting.direction,
          },
        },
      })
  }, [filterManager.params, page, sorting])

  const setPriority = useCallback(
    (id, value) => {
      dispatch({
        type: 'AGENCY/CLIENT_SET_PRIORITY',
        payload: { id, priority: value },
      })
    },
    [dispatch],
  )

  const breakpoints = useBreakpoint()

  const fetchSubRows = useCallback((id) => {
    dispatch({
      type: 'AGENCY/CLIENT_UPDATE',
      payload: { id, update: { subRowsLoading: true } },
    })

    AGENCY_API_V1.fetchClientAccounts({ id }).then(({ response }) => {
      dispatch({
        type: 'AGENCY/CLIENT_UPDATE',
        payload: {
          id,
          update: { subRows: response.data, subRowsLoading: undefined },
        },
      })
    })
  }, [])

  const applyAccountName = useCallback((id, name) => {
    dispatch({
      type: 'AGENCY/CLIENT_RENAME',
      payload: { id, data: { name } },
    })
  }, [])

  const { tableState, tableConfig } = useTableConfig(
    {
      RenderSubComponent: (props) => (
        <AccountRow
          {...props}
          emptyRowsPlaceholder={
            <Grid justifyContent={'center'}>{t('agency::list.accounts.empty')}</Grid>
          }
        />
      ),
      getRowCanExpand: () => true,
      setPriority,
      applyAccountName,
      switchUser: (email) => {
        history.push(pathGen(ROUTES.SERVICES))
        switchUser(email)
      },
      onFetchSubRows: fetchSubRows,
    },
    data,
  )

  return (
    <>
      <Grid
        justifyContent={'between'}
        alignItems={!breakpoints.md ? 'start' : 'center'}
        gap={16}
        direction={!breakpoints.md ? 'column' : 'row'}
        className={!breakpoints.fb ? 'margin-bottom-16' : 'margin-bottom-32'}
      >
        <PageHeader>{t('agency::navigation.clients')}</PageHeader>
        <Button
          size={'m'}
          component={Link}
          to={pathGen(ROUTES.AGENCY_ADD_CLIENT)}
          variant={'primary'}
          startIcon={<Icon name={'plus'} />}
          fullWidth={!breakpoints.md}
        >
          {t('agency::add_client')}
        </Button>
      </Grid>
      <FilterPanel
        filterManager={filterManager}
        className={styles['filter-panel']}
      />

      <Grid
        justifyContent={'start'}
        className={'margin-bottom-24'}
      >
        <DropdownSortingMenu
          sortingRuleMap={{
            priority: {
              asc: 'priority.asc',
              desc: 'priority.desc',
            },
            id: {
              asc: 'id.asc',
              desc: 'id.desc',
            },
            name: {
              asc: 'name.asc',
              desc: 'name.desc',
            },
          }}
          i18nKey={'agency::filters.sortmenu'}
          id={'advertisers-list-sort-menu'}
          sorting={sorting}
          onSortChange={(newSortingValue) => setSorting(newSortingValue)}
        />
      </Grid>

      <List
        table={tableState}
        config={tableConfig}
        EmptyComponent={() =>
          !loading && data.length === 0 ? (
            <EmptyResults
              header={t('agency::empty_list.clients.heading')}
              className={styles['agency-list__empty-results']}
            />
          ) : (
            <GridSpinner />
          )
        }
      />
      <ShowMoreButton
        onClick={handleChangePage}
        loading={loading}
        current={data.length}
        total={pagination.total}
      />
    </>
  )
}

export default Clients
